import { useState } from "react";
import { useNavigate } from "react-router";
import { Plug } from "lucide-react";
import {
  useCreateCollectionMutation,
  useGetConnectionsQuery,
  useSetCollectionProviderMutation,
} from "@/api/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../dialog";
import { SidebarGroupAction } from "../sidebar";
import { useIsOwnSpace, useMyCollections } from "@/api/hooks";
import { useAppSelector } from "@/state/redux/hooks";
import { selectMe } from "@/state/redux/slices/ui";
import { pluralize } from "@/common/utils";
import { providerLibrary } from "@/providers/external/registry";
import { ExternalProviderModule } from "@/providers/external/types";
import { cn } from "@/lib/utils";
import FormAlert from "./FormAlert";

/**
 * Creating a collection that mirrors an external system.
 *
 * Its own button rather than a section of the New collection dialog: naming it
 * is not a choice (a Gmail collection is called Gmail), filing it under the
 * collection currently open is not one either, and there is exactly one of
 * each per space -- so the whole flow is picking a system off a list, and it
 * reads as a different act from making a collection of your own.
 */
export default function ProviderCollectionCreate({
  className,
}: {
  /** Laid out in the flow of the list header, not in `SidebarGroupAction`'s
   * absolute corner slot. */
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  // Which one is being created, so the row it was clicked on can say so. Two
  // creates at once would be two roots on the same system.
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const me = useAppSelector(selectMe);
  const isOwnSpace = useIsOwnSpace();
  const navigate = useNavigate();

  const collections = useMyCollections();
  const [createCollection] = useCreateCollectionMutation();
  const [setCollectionProvider] = useSetCollectionProviderMutation();
  const { data: connections } = useGetConnectionsQuery(undefined, {
    skip: !isOwnSpace,
  });

  if (!me || !isOwnSpace || providerLibrary.length === 0) return null;

  /**
   * The root this space already has for a system, if any. Natural
   * subcollections carry the same `provider_key` as the root they belong to,
   * so only rows that head a tree count.
   */
  const rootFor = (provider: ExternalProviderModule) =>
    collections.find(
      (collection) =>
        collection.provider_key === provider.key &&
        !collection.provider_root_id,
    );

  const handleCreate = async (provider: ExternalProviderModule) => {
    if (pending || rootFor(provider)) return;
    setPending(provider.key);
    setError(null);
    try {
      const { id } = await createCollection({
        title: provider.title,
        provider_key: provider.key,
        provider_config: {},
        // The natural subcollections travel with the root so the whole set is
        // created in one transaction -- a Gmail collection with no categories
        // is not a half-finished thing the user can fix.
        provider_children: provider.naturalChildren.map((child) => ({
          title: child.title,
          provider_key: child.providerKey,
          provider_config: child.config,
        })),
      }).unwrap();
      // Reuse a grant the user already gave on this system. Asking again for
      // permission they have already granted is the thing this whole feature
      // exists not to do. A grant that predates this provider may not cover
      // what it needs; the collection then shows the connect prompt, and the
      // incremental consent screen lists only the new scope.
      const existing = connections?.find(
        (connection) =>
          connection.system_id === provider.systemId &&
          connection.status === "active" &&
          provider.scopes.every((scope) => connection.scopes.includes(scope)),
      );
      if (existing) {
        // Not unwrapped: if this fails the collection is simply unconnected,
        // and its page offers Connect -- which is the fix.
        await setCollectionProvider({
          collectionID: id,
          connection_id: existing.id,
          set_connection: true,
        });
      }
      setOpen(false);
      // Straight to the new collection: until it is connected there is nothing
      // to show anywhere else, and the Connect button lives on its page.
      navigate(`/${me.nickname?.nickname || me.id}/${id}`);
    } catch (e) {
      // The create itself failed -- most often the external systems cap.
      setError(e);
    } finally {
      setPending(null);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (pending) return;
        if (!next) setError(null);
        setOpen(next);
      }}
    >
      <DialogTrigger asChild>
        <SidebarGroupAction className={className} aria-label="Mirror a system">
          <Plug />
        </SidebarGroupAction>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Mirror a system</DialogTitle>
          <DialogDescription>
            A collection that shows what is in another account, on your
            timeline. Read-only, and you will be asked to grant access before
            anything is shown.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-1 py-2">
          {providerLibrary.map((provider) => {
            const Icon = provider.Icon;
            const existing = rootFor(provider);
            const disabled = !!existing || !!pending;
            return (
              <button
                key={provider.key}
                type="button"
                disabled={disabled}
                aria-disabled={disabled}
                onClick={() => handleCreate(provider)}
                className={cn(
                  "flex items-start gap-2 rounded-md border border-transparent px-2 py-2 text-left text-sm",
                  disabled
                    ? "cursor-not-allowed opacity-60"
                    : "hover:bg-row-hover",
                )}
              >
                <Icon className="mt-0.5 h-4 w-4 shrink-0" />
                <span className="min-w-0 flex-1">
                  <span className="block">{provider.title}</span>
                  <span className="block text-xs text-muted-foreground">
                    {existing
                      ? "Already added"
                      : pending === provider.key
                        ? "Creating…"
                        : provider.description}
                  </span>
                </span>
                {/* Not every system has a natural partition to offer:
                    Calendar's is the account's own list of calendars, which
                    nothing here can know before the grant. */}
                {!existing && provider.naturalChildren.length > 0 && (
                  <span className="shrink-0 pt-0.5 text-xs text-muted-foreground">
                    {pluralize(
                      provider.naturalChildren.length,
                      "subcollection",
                    )}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <FormAlert error={error} />
      </DialogContent>
    </Dialog>
  );
}
